import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Descriptions, Space, message, Modal, Input } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Content, Loader, PageSectionHeader } from '@/components';
import { NAMESPACE, COLOR } from '@/constants';
import { PRIVATE_PATH } from '@/constants/paths';
import {
  useGetCalculatorPackageByIdQuery,
  useUpdateCalculatorPackageMutation,
  useDeleteCalculatorPackageMutation,
  calculatorPackagesApi
} from '@/api/calculatorPackages';
import { buildCalculatorPackageWriteBody } from '@/api/calculatorPackageMappers';
import { packageBudgetPersistedMismatch } from '@/api/calculatorPackagePersistedCheck';
import type { CreateCalculatorPackageBody } from '@/api/calculatorPackages';
import { exportDomElementToPdf } from '@/utils';
import dayjs from 'dayjs';
import type { CalculatorPackageRow } from '@/api/calculatorPackages';
import { ConfirmModal } from '@/components';
import { useTheme } from 'styled-components';
import { useEU6Calculator } from '@/pages/EU6Calculator/hooks/useEU6Calculator';
import type { RateCard, RateCardInput } from '@/pages/EU6Calculator/EU6Calculator.types';
import { Currency } from '@/pages/EU6Calculator/EU6Calculator.types';
import { RatesTable, KPISummary, AddRateModal, CalculatorSettings } from '@/pages/EU6Calculator/components';
import { isTotalDeliveryPercentageOver100 } from '@/pages/EU6Calculator/sumRateCardsDeliveryPercentage';
import {
  calculatorSettingsFromPackage,
  fxRatesFromPackage,
  tradingDealSettingsFromPackage,
  packageBuyingPointForApi,
  displayBuyingPointForPackage,
  parsePackageNumber
} from '@/pages/EU6Calculator/calculatorSettingsFromPackage';
import type { CalculatorSettings as CalculatorSettingsState } from '@/pages/EU6Calculator/EU6Calculator.types';
import { useGetTradingDealsQuery } from '@/api';
import { PackagePrintArea, PackageMetaPanel, PackageCalculatorSection, ActionBar, ActionGroup, SecondaryButton, DangerButton } from './CustomPackageDetail.styles';

function formatDate(value?: string) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return value;
  }
}

type PackageEditorProps = {
  id: string;
  pkg: any;
  isFetching: boolean;
  refetch: () => void;
  onReset: () => void;
};

const PackageEditor: FC<PackageEditorProps> = ({ id, pkg, isFetching, refetch, onReset }) => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const printAreaRef = useRef<HTMLDivElement>(null);
  const { color } = useTheme();
  const { ConfirmModalTitle } = ConfirmModal;
  const [deletePackage] = useDeleteCalculatorPackageMutation();
  const [updatePackage, { isLoading: isSaving }] = useUpdateCalculatorPackageMutation();
  const { data: tradingDealsData } = useGetTradingDealsQuery();
  // Memoised so the empty fallback keeps one identity for the hooks that depend on it.
  const tradingDeals = useMemo(() => tradingDealsData?.tradingDeals ?? [], [tradingDealsData]);

  const calculatorOptions = useMemo(() => {
    const rows = pkg?.rows ?? [];
    const initialRateCards = rows.length
      ? rows.map((r: CalculatorPackageRow) => ({
          country: r.country,
          channel: r.channel,
          publisher: r.publisher,
          format: r.format,
          deliveryPercentage: Number(r.deliveryPercentage) || 0,
          rowCurrency: (String(r.rowCurrency).toUpperCase() as Currency) || Currency.GBP,
          buyCPM: Number(r.buyCPM) || 0,
          dspFeeId: null,
          dspFeeName: r.dspName ?? '',
          dspFeePercentage: Number(r.dspFeePercentage) || 0
        }))
      : undefined;
    const resetSeed = `${pkg._id}-${isEditing ? 'edit' : String(pkg.updatedAt ?? '')}`;

    return {
      initialRateCards,
      initialSettings: calculatorSettingsFromPackage(pkg, tradingDeals),
      initialFxRates: fxRatesFromPackage(pkg),
      resetSeed
    };
  }, [pkg, tradingDeals, isEditing]);

  const {
    settings,
    fxRates,
    rateCards,
    kpiMetrics,
    isModalOpen,
    updateSettings,
    updateFXRates,
    addRateCard,
    duplicateRateCard,
    updateRateCard,
    deleteRateCard,
    openModal,
    closeModal
  } = useEU6Calculator(calculatorOptions);

  const [editedName, setEditedName] = useState(() => String(pkg?.name ?? ''));
  const [editedDescription, setEditedDescription] = useState(() => String(pkg?.description ?? ''));

  useEffect(() => {
    if (!isEditing) {
      setEditedName(String(pkg?.name ?? ''));
      setEditedDescription(String(pkg?.description ?? ''));
    }
  }, [pkg?.name, pkg?.description, pkg?._id, isEditing]);

  useEffect(() => {
    if (!pkg || isEditing || tradingDeals.length === 0) return;
    const next = tradingDealSettingsFromPackage(pkg, tradingDeals);
    (Object.keys(next) as (keyof CalculatorSettingsState)[]).forEach((key) => {
      const value = next[key];
      if (value !== undefined) updateSettings(key, value as never);
    });
  }, [pkg, isEditing, tradingDeals, updateSettings]);

  const [editingRateCard, setEditingRateCard] = useState<RateCard | null>(null);

  useEffect(() => {
    if (!isModalOpen) setEditingRateCard(null);
  }, [isModalOpen]);

  const totalDeliveryPercentage = rateCards.reduce((sum, r) => sum + (Number(r.deliveryPercentage) || 0), 0);
  const otherDeliveryPercentageTotal = totalDeliveryPercentage - (editingRateCard?.deliveryPercentage ?? 0);
  const maxDeliveryPercentage = Math.max(0, 100 - otherDeliveryPercentageTotal);

  const handleDelete = () => {
    Modal.confirm({
      title: (
        <ConfirmModalTitle style={{ color: COLOR.danger }}>Delete package</ConfirmModalTitle>
      ),
      icon: <ExclamationCircleOutlined style={{ color: color?.danger ?? '#ff4d4f' }} />,
      content: 'Are you sure you want to delete this package?',
      okText: t('cancel'),
      cancelButtonProps: { type: 'text', danger: true, style: { color: COLOR.danger } },
      cancelText: t('delete'),
      onCancel: () => {
        deletePackage(id)
          .unwrap()
          .then(() => {
            message.success(t('packageDeleted'));
            navigate(PRIVATE_PATH.CUSTOM_PACKAGE);
          })
          .catch(() => {});
      }
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRateCard(null);
    closeModal();
    onReset();
  };

  const handleSave = async () => {
    const nameTrim = editedName.trim();
    if (!nameTrim) {
      message.error(t('requiredFieldMessage'));
      return;
    }

    if (isTotalDeliveryPercentageOver100(rateCards)) {
      message.warning(t('totalDeliveryExceeds100'));
      return;
    }

    const rows: CalculatorPackageRow[] = rateCards.map((r) => ({
      country: r.country,
      channel: r.channel,
      publisher: r.publisher,
      format: r.format,
      deliveryPercentage: Number(r.deliveryPercentage) || 0,
      rowCurrency: String(r.rowCurrency),
      buyCPM: Number(r.buyCPM) || 0,
      dspFeePercentage: Number(r.dspFeePercentage) || 0,
      /* The platform a line priced from this package reports, on the media
         plan, the IO and the booking database. Dropping it left them blank. */
      dspName: r.dspFeeName || undefined
    }));

    const totalImpressions = rateCards.reduce(
      (sum, r) => sum + (Number.isFinite(r.impressions) ? r.impressions : 0),
      0
    );
    const totalMediaSpendPlan = rateCards.reduce(
      (sum, r) => sum + (Number.isFinite(r.totalMediaSpendPlan) ? r.totalMediaSpendPlan : 0),
      0
    );
    const blendedBuyCpm = totalImpressions > 0 ? (totalMediaSpendPlan / totalImpressions) * 1000 : 0;

    const updated = await updatePackage({
      packageId: id,
      body: buildCalculatorPackageWriteBody({
        name: nameTrim,
        description: editedDescription.trim(),
        channel: pkg.channel,
        rows,
        settings,
        fxRates,
        tradingDeals,
        blendedMarginPercent: kpiMetrics.blendedMarginPercentage,
        blendedProfit: kpiMetrics.totalGrossProfitPlan,
        blendedBuyCpm
      }) as unknown as CreateCalculatorPackageBody
    }).unwrap();

    dispatch(
      calculatorPackagesApi.util.updateQueryData('getCalculatorPackageById', id, () => updated)
    );

    message.success(t('packageSaveSuccess'));
    const sentBudget = Number(settings.totalBudget) || 0;
    if (packageBudgetPersistedMismatch(sentBudget, updated)) {
      message.warning(
        `Saved, but the server did not store total budget ${sentBudget.toLocaleString()}. ` +
          'Check POST/PUT response and MongoDB — see BACKEND_CALCULATOR_PACKAGES.md.'
      );
    }
    setIsEditing(false);
    onReset();
  };

  const handleDownloadPdf = useCallback(async () => {
    if (!pkg?.name) return;
    const el = printAreaRef.current;
    if (!el) {
      message.error(t('packagePdfDownloadFailed'));
      return;
    }
    setIsPdfExporting(true);
    const prevBg = el.style.background;
    const prevColor = el.style.color;
    el.style.background = '#ffffff';
    el.style.color = '#000000';
    try {
      const fileBase = `Package_${pkg.name}_${dayjs().format('YYYY-MM-DD')}`;
      await exportDomElementToPdf(el, fileBase);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      message.error(detail && detail !== '[object Object]' ? detail : t('packagePdfDownloadFailed'));
    } finally {
      el.style.background = prevBg;
      el.style.color = prevColor;
      setIsPdfExporting(false);
    }
  }, [pkg, t]);

  return (
    <>
      <PageSectionHeader>{(isEditing ? editedName : pkg.name) || 'Package'}</PageSectionHeader>
      <Content>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <ActionBar>
          <ActionGroup>
            {!isEditing ? (
              <Button type="primary" onClick={() => {
                setEditedName(String(pkg?.name ?? ''));
                setEditedDescription(String(pkg?.description ?? ''));
                setIsEditing(true);
              }}>
                {t('edit')}
              </Button>
            ) : (
              <>
                <SecondaryButton onClick={handleCancelEdit}>{t('cancel')}</SecondaryButton>
                <Button type="primary" loading={isSaving} onClick={handleSave}>
                  {t('save')}
                </Button>
                <SecondaryButton
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingRateCard(null);
                    openModal();
                  }}
                >
                  Add Rate
                </SecondaryButton>
              </>
            )}
            <DangerButton danger onClick={handleDelete}>
              {t('delete')}
            </DangerButton>
          </ActionGroup>
          <ActionGroup>
            <SecondaryButton
              loading={isPdfExporting}
              disabled={isPdfExporting}
              onClick={handleDownloadPdf}
            >
              {t('downloadPackagePdf')}
            </SecondaryButton>
            <SecondaryButton onClick={() => navigate(PRIVATE_PATH.CUSTOM_PACKAGE)}>
              Back to list
            </SecondaryButton>
          </ActionGroup>
        </ActionBar>

          <PackagePrintArea ref={printAreaRef}>
            <PackageMetaPanel>
              <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('packageName')}>
              {isEditing ? (
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder={t('packageName')}
                  maxLength={200}
                  showCount
                />
              ) : (
                pkg.name
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {isEditing ? (
                <Input.TextArea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  placeholder="Description (optional)"
                  rows={3}
                  maxLength={2000}
                  showCount
                />
              ) : pkg.description != null && pkg.description !== '' ? (
                pkg.description
              ) : (
                '—'
              )}
            </Descriptions.Item>
              <Descriptions.Item label={t('channel')}>{pkg.channel}</Descriptions.Item>
              <Descriptions.Item label={t('packageBuyingPoint')}>
                {displayBuyingPointForPackage(pkg, tradingDeals, settings)}
              </Descriptions.Item>
              {(() => {
                const budget = parsePackageNumber(pkg.totalBudget);
                if (budget == null) return null;
                return (
                  <Descriptions.Item label="Total budget">
                    {budget.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </Descriptions.Item>
                );
              })()}
              <Descriptions.Item label={t('createdDate')}>{formatDate(pkg.createdAt)}</Descriptions.Item>
              <Descriptions.Item label={t('updatedAt')}>
                {pkg.updatedAt ? formatDate(String(pkg.updatedAt)) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('updatedBy')}>
                {pkg.updatedBy != null && pkg.updatedBy !== '' ? pkg.updatedBy : '—'}
              </Descriptions.Item>
            </Descriptions>
            </PackageMetaPanel>

            <PackageCalculatorSection>
              {isEditing && (
                <CalculatorSettings
                  settings={settings}
                  fxRates={fxRates}
                  onUpdate={updateSettings}
                  onUpdateFXRates={updateFXRates}
                />
              )}
              <KPISummary
                metrics={kpiMetrics}
                currency={settings.displayCurrency}
                showDeliveryHint={isEditing}
              />
              <RatesTable
                rateCards={rateCards}
                currency={settings.displayCurrency}
                loading={isFetching}
                showActions={isEditing}
                onDelete={(rateId) => {
                  if (!isEditing) return;
                  deleteRateCard(rateId);
                }}
                onEdit={
                  isEditing
                    ? (rate) => {
                        setEditingRateCard(rate);
                        openModal();
                      }
                    : undefined
                }
                onDuplicate={isEditing ? duplicateRateCard : undefined}
              />
            </PackageCalculatorSection>
          </PackagePrintArea>
        </Space>
      </Content>

      <AddRateModal
        open={isModalOpen}
        onClose={closeModal}
        onAdd={(input) => {
          if (!isEditing) return;
          addRateCard(input);
        }}
        initialRateCard={editingRateCard}
        onEdit={(input) => {
          if (!isEditing || !editingRateCard) return;
          updateRateCard(editingRateCard.id, {
            country: input.country,
            channel: input.channel,
            publisher: input.publisher,
            format: input.format,
            deliveryPercentage: input.deliveryPercentage,
            rowCurrency: input.rowCurrency,
            buyCPM: input.buyCPM,
            dspFeeId: null,
            dspFeeName: input.dspFeeName,
            dspFeePercentage: input.dspFeePercentage
          });
          setEditingRateCard(null);
          closeModal();
        }}
        defaultCurrency={settings.displayCurrency}
        otherDeliveryPercentageTotal={otherDeliveryPercentageTotal}
        maxDeliveryPercentage={maxDeliveryPercentage}
        totalBudget={settings.totalBudget}
      />
    </>
  );
};

const CustomPackageDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (searchParams.get('edit') === '1') {
      setEditorKey((k) => k + 1);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: pkg, isLoading, isFetching, refetch } = useGetCalculatorPackageByIdQuery(id!, { skip: !id });

  if (!id) {
    navigate(PRIVATE_PATH.CUSTOM_PACKAGE);
    return null;
  }

  if (isLoading || !pkg) {
    return (
      <>
        <PageSectionHeader>{t('customPackagesTitle')}</PageSectionHeader>
        <Content>
          <Loader fullscreen={false} />
        </Content>
      </>
    );
  }

  return (
    <PackageEditor
      key={editorKey}
      id={id}
      pkg={pkg}
      isFetching={isFetching}
      refetch={refetch}
      onReset={() => setEditorKey((k) => k + 1)}
    />
  );
};

export default CustomPackageDetail;
